import {
  FC,
  KeyboardEvent as ReactKeyboardEvent,
  ReactNode,
  useEffect,
  useId,
  useRef,
} from 'react';
import { classnames } from '@/utils/functions';
import useMountTransition from '@/hooks/useMountTransition';
import useOutsideClickHandler from '@/hooks/useClickOutside';
import styles from './modal.module.scss';
import XmarkIcon from '@/assets/icons/xmark.svg?react';

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

/* Inputs the browser divides into parts, each its own Tab stop */
const SEGMENTED_INPUT_TYPES = [
  'date',
  'time',
  'datetime-local',
  'month',
  'week',
];
const isSegmentedInput = (element: Element | null): boolean =>
  element instanceof HTMLInputElement &&
  SEGMENTED_INPUT_TYPES.includes(element.type);

/* Visible controls in Tab order; a radio group is a single stop, as natively */
const getTabbable = (dialog: HTMLElement): HTMLElement[] =>
  Array.from(dialog.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
    (element) => {
      if (element.tabIndex < 0 || element.getClientRects().length === 0) {
        return false;
      }
      if (
        element instanceof HTMLInputElement &&
        element.type === 'radio' &&
        element.name
      ) {
        const group = Array.from(
          dialog.querySelectorAll<HTMLInputElement>(
            `input[type="radio"][name="${CSS.escape(element.name)}"]`
          )
        );
        const checked = group.find((radio) => radio.checked);
        return element === (checked ?? group[0]);
      }
      return true;
    }
  );

interface ModalProps {
  children: ReactNode;
  title?: string;
  /* Accessible name for a modal that has no visible title */
  ariaLabel?: string;
  isOpen: boolean;
  size?: 'small' | 'medium' | 'large';
  onClose?: () => void;
}

const Modal: FC<ModalProps> = ({
  children,
  title,
  ariaLabel,
  isOpen,
  onClose,
  size = 'medium',
}) => {
  const isCloseable = !!onClose;
  const closePopover = () => {
    if (isCloseable) {
      onClose();
    }
  };

  const { mounted, visible } = useMountTransition(isOpen, 350);
  const modalRef = useOutsideClickHandler<HTMLDivElement>(closePopover);
  const titleId = useId();

  // Read through a ref so inline onClose callbacks don't re-run the effect below on every render
  const closeRef = useRef(closePopover);
  closeRef.current = closePopover;

  // While open: close on Escape, then hand focus back to whatever opened the modal
  useEffect(() => {
    if (!isOpen) return;

    const opener = document.activeElement as HTMLElement | null;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeRef.current();
      }
    };
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);

      // Leave focus alone if the user already moved it somewhere else on the page
      const active = document.activeElement;
      const focusMovedElsewhere =
        !!active &&
        active !== document.body &&
        !active.closest('[role="dialog"]');
      if (!focusMovedElsewhere && opener?.isConnected) {
        opener.focus();
      }
    };
  }, [isOpen]);

  // Focus the dialog itself so screen readers announce its name before its contents
  useEffect(() => {
    if (isOpen && mounted) {
      modalRef.current?.focus();
    }
  }, [isOpen, mounted, modalRef]);

  // Keep a modal that is fading out from taking focus or clicks
  useEffect(() => {
    if (modalRef.current) {
      modalRef.current.inert = !isOpen;
    }
  }, [isOpen, mounted, modalRef]);

  /*
   * Moves focus on every Tab press instead of leaving it to the browser:
   * Safari skips buttons on Tab by default (unless "Press Tab to highlight
   * each item on a webpage" in its Advanced settings, or macOS Full Keyboard
   * Access, is on), which would let Tab leave the modal.
   *
   * Date fields are the exception: the browser steps Tab through their
   * month, day and year parts, which a jump to the next control would skip.
   * Their Tab is left alone, and focus is pulled back in if it leaves.
   */
  const trapFocus = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    const dialog = modalRef.current;
    if (event.key !== 'Tab' || !dialog) return;

    const tabbable = getTabbable(dialog);
    if (tabbable.length === 0) {
      event.preventDefault();
      return;
    }

    const last = tabbable.length - 1;
    const forward = !event.shiftKey;

    if (isSegmentedInput(document.activeElement)) {
      setTimeout(() => {
        if (!dialog.contains(document.activeElement)) {
          tabbable[forward ? 0 : last].focus();
        }
      }, 0);
      return;
    }

    event.preventDefault();
    const index = tabbable.indexOf(document.activeElement as HTMLElement);
    let next: number;
    if (!forward) {
      next = index <= 0 ? last : index - 1;
    } else {
      next = index === -1 || index === last ? 0 : index + 1;
    }
    tabbable[next].focus();
  };

  if (!mounted) return null;

  return (
    <>
      <div
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        aria-label={title ? undefined : ariaLabel}
        tabIndex={-1}
        onKeyDown={trapFocus}
        className={classnames(styles['modal'], {
          [styles['modal--hidden']]: !visible,
        })}
        data-size={size}
      >
        <div className={styles['modal-header']}>
          {title && <h2 id={titleId}>{title}</h2>}
          {isCloseable && (
            <button
              type="button"
              className={styles['close-button']}
              aria-label="Close"
              onClick={closePopover}
            >
              <XmarkIcon className={styles['close-xmark']} aria-hidden="true" />
            </button>
          )}
        </div>

        <div className={styles['modal-content']}>{children}</div>
      </div>

      <div
        className={`${styles['modal-backdrop']} ${
          visible
            ? styles['modal-backdrop--visible']
            : styles['modal-backdrop--hidden']
        }`}
      />
    </>
  );
};

export default Modal;
